import type { ReactNode } from 'react';
import { Add } from 'iconsax-react';

import { Button, type ButtonProps } from '@design-system/button';
import { cn } from '@design-system/helpers';
import { SearchInput } from '@design-system/molecules/search-input';

/** The row above a content tab's list: search, optional filters and the add button. */
export function ContentToolbar({
  placeholder,
  addLabel,
  onAdd,
  children,
  addVariant = 'primary',
}: {
  placeholder: string;
  addLabel: string;
  onAdd: () => void;
  /** Filter control, joined to the search like the omni list toolbar. */
  children?: ReactNode;
  /** Tia soft actions use `primaryPale`; Omni keeps solid `primary`. */
  addVariant?: ButtonProps['variant'];
}) {
  const search = (
    <SearchInput
      queryKey='search'
      placeholder={placeholder}
      aria-label={placeholder}
      className={cn(
        'min-w-[12rem] flex-1',
        children &&
          'z-0 rounded-none border-0 bg-primary-30 shadow-none ring-0 outline-none focus-within:border-0 focus-within:ring-0'
      )}
    />
  );

  return (
    <div
      data-slot='content-toolbar'
      className='flex shrink-0 flex-wrap items-center gap-2 px-4 py-3'
    >
      {children ? (
        <div className='flex h-10 min-w-0 flex-1 overflow-hidden rounded-xl border border-primary-200'>
          {search}
          <div className='flex h-full shrink-0 [&>div]:flex [&>div]:h-full'>
            {children}
          </div>
        </div>
      ) : (
        search
      )}
      <Button
        variant={addVariant}
        size='sm'
        className={cn(
          'rounded-[10px] pr-4 [&_svg]:size-5',
          addVariant === 'primaryPale' &&
            'border-0 bg-primary-100 text-primary-1000 hover:bg-primary-100/80'
        )}
        onClick={onAdd}
      >
        <Add aria-hidden='true' />
        {addLabel}
      </Button>
    </div>
  );
}
